// Bridges Clerk session data into AppContext.
// Must render inside both ClerkProvider and AppProvider.
// Reads user/org from Clerk hooks and pushes into the shared context
// so Sidebar, TopBar, and data hooks see the real identity.
//
// The "no active organization" gate is NOT in this file. Task 104d.1
// moved that check to `AppLayout` (see `src/layouts/AppLayout.tsx`),
// which redirects to `/no-organizations` when `currentOrg.id` is empty
// in live mode. This keeps the gate on a single router-aware seam and
// lets the invite-accept / no-orgs landing pages live outside the
// authenticated layout.

import { useEffect } from 'react';
import { useUser, useOrganization, useClerk, useAuth } from '@clerk/clerk-react';
import { useAppContext } from './AppContext';

export function ClerkSync({ children }: { children: React.ReactNode }) {
  const { user, isLoaded: userLoaded } = useUser();
  const { organization, isLoaded: orgLoaded } = useOrganization();
  const clerk = useClerk();
  const { getToken } = useAuth();
  const { setCurrentUser, setCurrentOrg, setSignOut, setGetToken, setReloadOrg } = useAppContext();

  useEffect(() => {
    if (!userLoaded || !user) return;
    setCurrentUser({
      id: user.id,
      name: user.fullName ?? user.primaryEmailAddress?.emailAddress ?? 'Unknown',
      email: user.primaryEmailAddress?.emailAddress ?? '',
      role: 'admin', // Clerk org role resolved below if org is loaded
      avatarUrl: user.imageUrl,
    });
  }, [userLoaded, user, setCurrentUser]);

  useEffect(() => {
    if (!orgLoaded || !organization) return;
    setCurrentOrg({
      id: organization.id,
      name: organization.name,
      slug: organization.slug ?? organization.id,
    });
  }, [orgLoaded, organization, setCurrentOrg]);

  useEffect(() => {
    setSignOut(() => clerk.signOut());
  }, [clerk, setSignOut]);

  useEffect(() => {
    setGetToken(() => getToken());
  }, [getToken, setGetToken]);

  useEffect(() => {
    setReloadOrg(async () => {
      // Ask Clerk's cached organization to refetch so the OrganizationSwitcher
      // and anything else reading useOrganization() sees the post-save name.
      await organization?.reload();
    });
  }, [organization, setReloadOrg]);

  // The no-organizations gate used to live here as an inline
  // OrganizationList. 104d.1 moves it into `AppLayout` so the redirect
  // has a proper route (`/no-organizations`), and so the landing pages
  // (`/invites/accept`, `/no-organizations`) render outside the app
  // shell. See layouts/AppLayout.tsx.
  return <>{children}</>;
}
